import { useEffect } from 'react';

// Minimal CSS injected into the TechDocs reader Shadow DOM, limited to notebook visuals
const CSS_TEXT = `
/* Keep shadow content background transparent to match host */
:host, :host > * { background: transparent !important; }

/* Hide until notebook styles/normalization applied */
body:not(.nb-ready) { visibility: hidden; }

/* Figures/images in notebook only */
.md-typeset .jp-Notebook figure { margin-left: auto; margin-right: auto; text-align: center; }
.md-typeset .jp-Notebook img { display: block; margin: 0 auto; max-width: 100%; height: auto; }

/* Notebook layout: prompts in fixed left column, content in right */
.md-typeset .jp-InputArea, .md-typeset .jp-OutputArea {
  display: grid;
  grid-template-columns: 5rem 1fr; /* prompt | content */
  align-items: start;
  column-gap: .75rem;
  border-left: none; padding-left: 0; margin-left: 0;
}
.md-typeset .jp-InputPrompt, .md-typeset .jp-OutputPrompt {
  grid-column: 1;
  text-align: left;
  padding-left: .25rem;
  color: var(--md-default-fg-color--light);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  user-select: none;
  white-space: nowrap; /* single-line prompts */
  font-size: .85rem; line-height: 1.2;
}
/* Jupyter prompt colors */
.md-typeset .jp-InputPrompt { color: #307fc1 !important; }
.md-typeset .jp-OutputPrompt { color: #bf5b3d !important; }

/* Place notebook contents in second column */
.md-typeset .jp-InputArea > :not(.jp-InputPrompt),
.md-typeset .jp-OutputArea > :not(.jp-OutputPrompt) { grid-column: 2; min-width: 0; }
/* Ensure wrapper doesn't break grid alignment */
.md-typeset .jp-OutputArea-child { display: contents; }
/* Spacing between input and output blocks */
.md-typeset .jp-OutputArea { margin-top: .6rem; }

/* Hide mkdocs-jupyter duplicate/plain copies and its copy UI inside notebook blocks */
.md-typeset .highlight-ipynb .clipboard-copy-txt,
.md-typeset .highlight-ipynb .clipboard-copy,
.md-typeset .highlight-ipynb .clipboard-copy-button,
.md-typeset .highlight-ipynb .clipboard-copy-icon,
.md-typeset .highlight-ipynb .notice,
.md-typeset .jp-Notebook .clipboard-copy-txt,
.md-typeset .jp-Notebook .clipboard-copy,
.md-typeset .jp-Notebook .clipboard-copy-button,
.md-typeset .jp-Notebook .clipboard-copy-icon,
.md-typeset .jp-Notebook .notice { display: none !important; }

/* Hide duplicate plaintext pre that can follow a highlighted ipynb block when include_source is on */
.md-typeset .highlight-ipynb + pre { display: none !important; }

/* Ensure notebook highlight container can host our copy button */
.md-typeset .highlight-ipynb, .md-typeset .jp-Notebook pre { position: relative; }

/* Custom notebook-only copy button + toast (does not affect global code blocks) */
.nb-clipboard {
  position: absolute; top: .4rem; right: .4rem;
  width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--md-default-fg-color--lighter); border-radius: 4px;
  color: var(--md-default-fg-color); background: var(--md-default-bg-color); cursor: pointer; opacity: .85;
}
.nb-clipboard:hover { opacity: 1; }
.nb-clipboard svg { width: 18px; height: 18px; }
.nb-copy-toast {
  position: absolute; top: .4rem; right: 2.9rem;
  padding: 2px 6px; font-size: 12px; border-radius: 3px;
  background: var(--md-code-bg-color); color: var(--md-code-fg-color);
  border: none; box-shadow: none; opacity: 0; transform: translateY(-4px);
  transition: opacity .2s ease, transform .2s ease; pointer-events: none;
}
.nb-copy-toast.show { opacity: 1; transform: translateY(0); }

/* Dataframes only: zebra rows and three-line style (scoped to notebook output) */
.md-typeset .jp-OutputArea .dataframe { width: auto; max-width: 100%; overflow-x: auto; margin: 0.75rem auto; }
.md-typeset .jp-OutputArea .dataframe table { width: auto; max-width: 100%; border-collapse: collapse; border-spacing: 0; margin: 0; border-left: none; border-right: none; border-top: 1px solid var(--md-typeset-table-color); border-bottom: 1px solid var(--md-typeset-table-color); }
.md-typeset .jp-OutputArea .dataframe thead th { background: var(--md-default-bg-color--light); color: var(--md-default-fg-color); font-weight: 600; border-bottom: 1px solid var(--md-default-fg-color--lighter); }
.md-typeset .jp-OutputArea .dataframe th, .md-typeset .jp-OutputArea .dataframe td { border: none; text-align: left; padding: .45rem .6rem; vertical-align: top; white-space: nowrap; }
.md-typeset .jp-OutputArea .dataframe tbody tr:nth-child(2n) { background: var(--md-typeset-table-color--light); }

/* Ensure span tokens don't collapse whitespace */
.md-typeset .highlight-ipynb pre span, .md-typeset .jp-Notebook pre span { white-space: pre; }

/* Expanded syntax coloring within notebook code blocks (Pygments token classes) */
/* Keywords */
.md-typeset .highlight-ipynb pre .k, .md-typeset .jp-Notebook pre .k,
.md-typeset .highlight-ipynb pre .kc, .md-typeset .jp-Notebook pre .kc,
.md-typeset .highlight-ipynb pre .kd, .md-typeset .jp-Notebook pre .kd,
.md-typeset .highlight-ipynb pre .kn, .md-typeset .jp-Notebook pre .kn,
.md-typeset .highlight-ipynb pre .kp, .md-typeset .jp-Notebook pre .kp,
.md-typeset .highlight-ipynb pre .kr, .md-typeset .jp-Notebook pre .kr,
.md-typeset .highlight-ipynb pre .kt, .md-typeset .jp-Notebook pre .kt { color: #3f6ec6 !important; font-weight: 600; }
/* Names */
.md-typeset .highlight-ipynb pre .n, .md-typeset .jp-Notebook pre .n,
.md-typeset .highlight-ipynb pre .na, .md-typeset .jp-Notebook pre .na,
.md-typeset .highlight-ipynb pre .nb, .md-typeset .jp-Notebook pre .nb,
.md-typeset .highlight-ipynb pre .bp, .md-typeset .jp-Notebook pre .bp,
.md-typeset .highlight-ipynb pre .nc, .md-typeset .jp-Notebook pre .nc,
.md-typeset .highlight-ipynb pre .nn, .md-typeset .jp-Notebook pre .nn,
.md-typeset .highlight-ipynb pre .ne, .md-typeset .jp-Notebook pre .ne { color: #0c3172 !important; }
/* Functions */
.md-typeset .highlight-ipynb pre .nf, .md-typeset .jp-Notebook pre .nf { color: #a846b9 !important; }
/* Strings */
.md-typeset .highlight-ipynb pre .s, .md-typeset .jp-Notebook pre .s,
.md-typeset .highlight-ipynb pre .s1, .md-typeset .jp-Notebook pre .s1,
.md-typeset .highlight-ipynb pre .s2, .md-typeset .jp-Notebook pre .s2,
.md-typeset .highlight-ipynb pre .sa, .md-typeset .jp-Notebook pre .sa,
.md-typeset .highlight-ipynb pre .sb, .md-typeset .jp-Notebook pre .sb,
.md-typeset .highlight-ipynb pre .sc, .md-typeset .jp-Notebook pre .sc,
.md-typeset .highlight-ipynb pre .sd, .md-typeset .jp-Notebook pre .sd,
.md-typeset .highlight-ipynb pre .se, .md-typeset .jp-Notebook pre .se,
.md-typeset .highlight-ipynb pre .si, .md-typeset .jp-Notebook pre .si,
.md-typeset .highlight-ipynb pre .sr, .md-typeset .jp-Notebook pre .sr { color: #1c7d4d !important; }
/* Numbers */
.md-typeset .highlight-ipynb pre .m, .md-typeset .jp-Notebook pre .m,
.md-typeset .highlight-ipynb pre .mi, .md-typeset .jp-Notebook pre .mi,
.md-typeset .highlight-ipynb pre .mf, .md-typeset .jp-Notebook pre .mf { color: #d52a2a !important; }
/* Comments */
.md-typeset .highlight-ipynb pre .c, .md-typeset .jp-Notebook pre .c,
.md-typeset .highlight-ipynb pre .cm, .md-typeset .jp-Notebook pre .cm,
.md-typeset .highlight-ipynb pre .ch, .md-typeset .jp-Notebook pre .ch,
.md-typeset .highlight-ipynb pre .cp, .md-typeset .jp-Notebook pre .cp,
.md-typeset .highlight-ipynb pre .cpf, .md-typeset .jp-Notebook pre .cpf { color: #6b7280 !important; font-style: italic; }
/* Operators & punctuation */
.md-typeset .highlight-ipynb pre .o, .md-typeset .jp-Notebook pre .o,
.md-typeset .highlight-ipynb pre .ow, .md-typeset .jp-Notebook pre .ow,
.md-typeset .highlight-ipynb pre .p, .md-typeset .jp-Notebook pre .p { color: var(--md-default-fg-color--light) !important; }
`;

export const NotebookStylesAddon = () => {
  useEffect(() => {
    let observers: MutationObserver[] = [];
    const attached = new WeakSet<ShadowRoot>();

    const ensureStyleIn = (root: ShadowRoot) => {
      const head = root.querySelector('head') as HTMLHeadElement | null;
      const target: ParentNode & Element = (head ?? (root as unknown as Element));
      if (!target.querySelector('#notebook-styles-injected')) {
        const style = document.createElement('style');
        style.id = 'notebook-styles-injected';
        style.textContent = CSS_TEXT;
        target.appendChild(style);
      }
    };

    // Tokenize python minimally to Pygments-like classes if no spans
    const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const tokenizePython = (codeEl: HTMLElement) => {
      if (codeEl.querySelector('span')) return; // already tokenized
      const text = codeEl.textContent ?? '';
      let html = escapeHtml(text);
      // Comments
      html = html.replace(/(^|\n)([\t ]*#.*)(?=$|\n)/g, (_m, p1, p2) => `${p1}<span class="c">${p2}</span>`);
      // Triple-quoted strings
      html = html.replace(/("""[\s\S]*?"""|'''[\s\S]*?''')/g, '<span class="sd">$1</span>');
      // Strings
      html = html.replace(/("[^"\n]*"|'[^'\n]*')/g, '<span class="s">$1</span>');
      // Numbers
      html = html.replace(/\b\d+(?:\.\d+)?\b/g, '<span class="m">$&</span>');
      // Keywords
      const kw = ['False','class','finally','is','return','None','continue','for','lambda','try','True','def','from','nonlocal','while','and','del','global','not','with','as','elif','if','or','yield','assert','else','import','pass','break','except','in','raise'];
      html = html.replace(new RegExp(`\\b(${kw.join('|')})\\b`, 'g'), '<span class="k">$1</span>');
      // Builtins (nb)
      const nb = ['print','len','range','dict','list','set','tuple','int','float','str','map','filter','zip'];
      html = html.replace(new RegExp(`\\b(${nb.join('|')})\\b`, 'g'), '<span class="nb">$1</span>');
      // Punctuation
      html = html.replace(/([:,\[\]\{\}\(\)])/g, '<span class="p">$1</span>');
      codeEl.innerHTML = html;
    };

    // Normalize notebook code blocks to match native markup where possible
    const normalizeNotebookCodeBlocks = (root: ShadowRoot) => {
      const preNodes = Array.from(root.querySelectorAll('.highlight-ipynb pre, .jp-Notebook pre')) as HTMLPreElement[];
      for (const pre of preNodes) {
        // If a code child exists, preserve existing HTML (including token spans)
        let code = pre.querySelector('code');
        if (!code) {
          const existingHtml = pre.innerHTML;
          const hasTokenSpans = /<span[\s>]/.test(existingHtml);
          code = document.createElement('code');
          if (hasTokenSpans) {
            code.innerHTML = existingHtml; // preserve tokens
          } else {
            code.textContent = pre.textContent ?? '';
          }
          pre.innerHTML = '';
          pre.appendChild(code);
        }
        // Try to add language- class if we can infer it from parent classes (e.g., hl-python)
        const parentClass = pre.parentElement?.className || '';
        const m = parentClass.match(/hl-([a-z0-9]+)/i);
        if (m && !code.className.includes('language-')) {
          code.classList.add(`language-${m[1].toLowerCase()}`);
        }
        // Ensure parent has highlight class for styling consistency
        const container = pre.parentElement;
        if (container && !container.classList.contains('highlight')) {
          container.classList.add('highlight');
        }
        // Client-side tokenization for python if no spans
        if (code.classList.contains('language-python')) tokenizePython(code as HTMLElement);
      }
      // Mark ready after normalization
      root.querySelector('body')?.classList.add('nb-ready');
    };

    // Show a toast near the notebook copy button
    const showToast = (container: Element, message: string) => {
      let toast = container.querySelector('.nb-copy-toast') as HTMLSpanElement | null;
      if (!toast) {
        toast = document.createElement('span');
        toast.className = 'nb-copy-toast';
        container.appendChild(toast);
      }
      toast.textContent = message;
      toast.classList.add('show');
      window.setTimeout(() => toast && toast.classList.remove('show'), 1200);
    };

    // Add a copy button fallback for notebook code blocks only on the highlight container
    const attachCopyButtons = (root: ShadowRoot) => {
      const containers = Array.from(root.querySelectorAll('.highlight-ipynb')) as HTMLElement[];
      for (const el of containers) {
        // If native copy button exists, skip fallback
        if (el.querySelector('.md-clipboard')) continue;
        // Remove duplicate existing nb-clipboard buttons if any
        const existing = Array.from(el.querySelectorAll('.nb-clipboard'));
        if (existing.length > 1) {
          existing.slice(1).forEach(b => b.remove());
        }
        if (existing.length >= 1) continue;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'nb-clipboard';
        btn.setAttribute('aria-label', 'Copy to clipboard');
        btn.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true" width="18" height="18"><path fill="currentColor" fill-rule="evenodd" d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z"></path><path fill="currentColor" fill-rule="evenodd" d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z"></path></svg>';
        btn.addEventListener('click', () => {
          const pre = el.querySelector('pre') as HTMLPreElement | null;
          const text = pre?.innerText ?? '';
          if (!text) return;
          navigator.clipboard?.writeText(text).then(() => {
            showToast(el, 'Copied!');
          }).catch(() => {
            try {
              const sel = window.getSelection();
              if (!sel) return;
              const range = document.createRange();
              range.selectNodeContents(pre!);
              sel.removeAllRanges(); sel.addRange(range);
              document.execCommand('copy');
              sel.removeAllRanges();
              showToast(el, 'Copied!');
            } catch {}
          });
        });
        el.appendChild(btn);
      }
    };

    const attachObserver = (root: ShadowRoot) => {
      if (attached.has(root)) return;
      attached.add(root);
      ensureStyleIn(root);
      normalizeNotebookCodeBlocks(root);
      attachCopyButtons(root);
      const ob = new MutationObserver(() => {
        ensureStyleIn(root);
        normalizeNotebookCodeBlocks(root);
        attachCopyButtons(root);
      });
      ob.observe(root, { childList: true, subtree: true });
      observers.push(ob);
    };

    const scanAndAttach = () => {
      const hosts = Array.from(document.querySelectorAll('div[data-testid="techdocs-native-shadowroot"]')) as HTMLElement[];
      for (const host of hosts) {
        const sr = host.shadowRoot;
        if (sr) attachObserver(sr);
      }
    };

    // Initial scan
    scanAndAttach();

    // Always observe document for SPA navigation changes
    const docObserver = new MutationObserver(() => scanAndAttach());
    docObserver.observe(document.body, { childList: true, subtree: true });
    observers.push(docObserver);

    // Also try on history navigation
    const onPop = () => scanAndAttach();
    window.addEventListener('popstate', onPop);

    return () => {
      observers.forEach(o => o.disconnect());
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  return null;
};
